/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { useStorefrontData } from '../context/StorefrontDataContext';

const LOGO_PATHS = [
  {
    letter: 'S',
    d: 'M83.6292 106.509C83.6292 122.814 89.6779 134.648 101.775 142.012C113.872 150.953 139.645 160.158 179.093 169.625C220.644 179.619 250.888 193.294 269.823 210.651C289.283 228.008 299.014 252.992 299.014 285.602C299.014 319.79 286.128 347.403 260.355 368.442C235.109 389.481 201.972 400 160.947 400C101.512 400 47.8633 378.961 0 336.884L41.0256 287.968C80.9994 322.157 121.499 339.251 162.525 339.251C183.038 339.251 199.606 334.78 212.229 325.838C224.852 316.371 231.164 304.274 231.164 289.546C231.164 274.819 225.378 263.248 213.807 254.832C202.761 246.417 182.775 238.79 153.846 231.953C103.879 220.381 69.165 206.706 49.7042 190.927C27.0874 173.57 15.7791 147.009 15.7791 111.243C15.7791 76.5286 28.4024 49.4412 53.6489 29.9803C78.8955 9.99344 111.243 0 150.69 0C203.813 0 249.31 15.5161 287.18 46.5483L252.466 95.4635C240.894 84.9441 225.378 76.5286 205.917 70.217C186.456 63.3794 167.258 59.9606 148.323 59.9606C128.863 59.9606 113.084 64.1683 100.986 72.5838C89.4149 80.4734 83.6292 91.7817 83.6292 106.509Z',
  },
  {
    letter: 'E1',
    d: 'M674.184 9.46533V70.2149H468.266V172.779H652.882V230.373H468.266V333.726H680.495V395.264H402.783V9.46533H674.184Z',
  },
  {
    letter: 'C',
    d: 'M1071.52 288.757L1113.34 331.361C1073.36 377.12 1023.92 400 965.015 400C906.633 400 857.98 381.328 819.059 343.984C780.663 306.114 761.465 258.514 761.465 201.183C761.465 143.327 781.189 95.4635 820.636 57.5937C860.084 19.1979 909.788 0 969.749 0C1028.66 0 1078.36 22.0907 1118.86 66.2722L1077.84 111.243C1059.43 93.3596 1042.07 80.9994 1025.76 74.1617C1009.46 67.3241 990.262 63.9053 968.171 63.9053C928.723 63.9053 895.587 76.5286 868.763 101.775C842.464 127.022 829.315 159.106 829.315 198.028C829.315 236.423 842.464 269.034 868.763 295.858C895.061 322.682 926.356 336.095 962.648 336.095C984.739 336.095 1003.67 332.413 1019.45 325.049C1035.76 317.686 1053.12 305.588 1071.52 288.757Z',
  },
  {
    letter: 'E2',
    d: 'M1487.63 9.46533V70.2149H1281.71V172.779H1466.33V230.373H1281.71V333.726H1493.94V395.264H1216.23V9.46533H1487.63Z',
  },
  {
    letter: 'R',
    d: 'M1825.01 254.83L1925.99 395.264H1843.15L1750.85 265.087H1665.64V395.264H1600.16V9.46533H1743.75C1802.13 9.46533 1843.94 19.4587 1869.19 39.4456C1894.96 58.9065 1907.85 90.7277 1907.85 134.909C1907.85 198.551 1880.24 238.525 1825.01 254.83ZM1746.11 206.704C1782.4 206.704 1807.39 201.181 1821.06 190.136C1834.74 179.091 1841.58 160.419 1841.58 134.12C1841.58 108.874 1834.74 91.7796 1821.06 82.8381C1807.39 73.3707 1782.93 68.6369 1747.69 68.6369H1665.64V206.704H1746.11Z',
  },
  {
    letter: 'A',
    d: 'M2079.63 307.69L2040.97 395.264H1971.54L2141.95 9.46533H2211.38L2381.01 395.264H2311.58L2272.92 307.69H2079.63ZM2246.88 246.941L2176.67 88.3608L2106.45 246.941H2246.88Z',
  }
];

export default function EditorialLoader() {
  const { isReady } = useStorefrontData();
  const shouldReduceMotion = useReducedMotion();
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof window !== 'undefined') {
      return !sessionStorage.getItem('secera_has_loaded');
    }
    return true;
  });

  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  useEffect(() => {
    if (!isVisible) return;

    // Lock scrolling
    document.documentElement.classList.add('no-scroll');

    // Guarantee a minimum of 1.2s to render the complete entrance sequence beautifully
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 1200);

    return () => {
      clearTimeout(timer);
    };
  }, [isVisible]);

  // Once both storefront data is ready and the minimum display timer has elapsed, start exit
  useEffect(() => {
    if (isReady && minTimeElapsed) {
      const exitTimer = setTimeout(() => {
        setIsVisible(false);
        // Release scrolling
        document.documentElement.classList.remove('no-scroll');
        sessionStorage.setItem('secera_has_loaded', 'true');
      }, 200); // Small buffer to ensure visual smoothness

      return () => clearTimeout(exitTimer);
    }
  }, [isReady, minTimeElapsed]);

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={
            shouldReduceMotion
              ? { opacity: 0 }
              : {
                y: '-100%',
                transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
              }
          }
          className="fixed inset-0 z-[9999] bg-paper flex items-center justify-center pointer-events-auto select-none"
        >
          {/* Subtle horizontal division lines to honor Flat Doctrine & 1px borders */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[1px] bg-ink/5 pointer-events-none" />

          <div className="relative w-full max-w-[280px] sm:max-w-[360px] md:max-w-[420px] px-8">
            <svg
              viewBox="0 0 2382 400"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-auto overflow-visible"
            >
              {LOGO_PATHS.map((path, index) => {
                const staggerDelay = shouldReduceMotion ? 0 : index * 0.1;
                return (
                  <motion.path
                    key={path.letter}
                    d={path.d}
                    fill="#722F38"
                    initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 15 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.6,
                        delay: staggerDelay,
                        ease: [0.16, 1, 0.3, 1]
                      }
                    }}
                  />
                );
              })}
            </svg>
          </div>

          {/* Discreet Loading Indicator at Bottom (Responsive Single-line Layout) */}
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-2 w-full px-4 text-center">
            <span className="text-label text-brand-wine opacity-60 tracking-[0.15em] sm:tracking-[0.25em] text-[8px] sm:text-[9px] whitespace-nowrap">
              Hari Spesial, Bersama Secera.
            </span>
            <div className="w-16 h-[1px] bg-ink/10 overflow-hidden relative">
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{
                  repeat: Infinity,
                  duration: 1.5,
                  ease: 'linear'
                }}
                className="absolute inset-0 bg-brand-wine w-8"
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
